import {
  getValidConvertibleInstanceAction,
  QUESTION_SET,
  TRANSACTION_TYPE
} from 'actions/getValidConvertibleInstanceAction';
import { openCustomerFeatureNotAvailableModal, openFeatureNotAvailableModal } from 'actions/openModalAction';
import { analyticsUserType, GTM_EVENT_TYPES, Q_INTERACTION_TYPES, sendAnalytics } from 'analytics';
import { ExternalLink } from 'components/shared/externalLink/externalLink';
import { getConfig } from 'config/configuration';
import { History } from 'history';
import { AuthType } from 'interfaces/authTypes';
import { POLICY_LIFECYCLE_STATUS, PolicyLifecycleStatus } from 'interfaces/customerDataTypes';
import { EXTERNAL_PATH, PATH } from 'interfaces/pathTypes';
import { Link } from 'react-router-dom';
import { AppDispatch } from 'store/configureStore';
import { ActionButtonProps } from './policyStatus';

const { assetsUrl, customerRenewalEnabled } = getConfig();

interface LinkConfigProps {
  isAgent: boolean;
  policyRef: string;
  dispatch: AppDispatch;
  history: History;
  authType: AuthType;
}

interface HelperTextProps {
  policyRef: string;
  daysUntilRenewal: number;
}

export interface PolicyStatusDisplay {
  iconUrl: string;
  getHelperText?: (props: HelperTextProps) => JSX.Element;
  getActionButtonConfig?: (props: LinkConfigProps) => ActionButtonProps[];
  showRenewalDate: boolean;
  documentsLinkText?: string;
  showAutoRenewalPreference: boolean;
  className?: string;
  statusText: string;
}

export const getStatusDetails = (
  policyStatus: PolicyLifecycleStatus,
  stopRenewal: boolean,
  username: string
): PolicyStatusDisplay => {
  switch (policyStatus) {
    case POLICY_LIFECYCLE_STATUS.POLICY:
    case POLICY_LIFECYCLE_STATUS.POLICY_NB_SCHEDULED:
    case POLICY_LIFECYCLE_STATUS.POLICY_NB_COOLING:
    case POLICY_LIFECYCLE_STATUS.POLICY_MTAQUOTE:
    case POLICY_LIFECYCLE_STATUS.POLICY_MTAQUOTEINVITED:
    case POLICY_LIFECYCLE_STATUS.POLICY_MTA_NOCONVERSION:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-active.svg',
        showRenewalDate: true,
        showAutoRenewalPreference: true,
        className: 'policy-status-text-standard',
        statusText: 'Policy active'
      };

    case POLICY_LIFECYCLE_STATUS.POLICY_MTA_SCHEDULED:
    case POLICY_LIFECYCLE_STATUS.POLICY_MTA_COOLING:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-active.svg',
        showRenewalDate: true,
        showAutoRenewalPreference: true,
        className: 'policy-status-text-standard',
        statusText: 'Policy adjusted'
      };

    case POLICY_LIFECYCLE_STATUS.POLICY_RENEWAL_COOLING:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-active.svg',
        getHelperText: () => <p>Thank you for renewing your home insurance with Homeprotect.</p>,
        showRenewalDate: true,
        showAutoRenewalPreference: true,
        documentsLinkText: 'Renewal documents',
        getActionButtonConfig: ({ isAgent, dispatch }: LinkConfigProps) =>
          isAgent
            ? [
                {
                  text: 'Make changes',
                  to: '',
                  id: 'policy-button-secondary',
                  onClick: () => {
                    sendAnalytics({
                      event: GTM_EVENT_TYPES.QINTERACTION,
                      detail: {
                        qinteraction_type: Q_INTERACTION_TYPES.PRIMARY_ACTION_BUTTON_CLICK,
                        qinteraction_locator: 'dashboard_cta',
                        qinteraction_value: 'make_changes',
                        usertype: analyticsUserType(isAgent)
                      }
                    });
                    dispatch(openFeatureNotAvailableModal(isAgent));
                  }
                }
              ]
            : [],
        className: 'policy-status-text-standard',
        statusText: 'Policy renewed'
      };

    case POLICY_LIFECYCLE_STATUS.POLICY_RENEWAL_INVITE:
      return stopRenewal
        ? {
            iconUrl: assetsUrl + 'website/svg/policy-action-required.svg',
            getHelperText: ({ daysUntilRenewal, policyRef }: HelperTextProps) => (
              <p>
                This policy is set to expire in {daysUntilRenewal} days. Update your{' '}
                <Link to={`${PATH.SETTINGS}/policy/${policyRef}`}>auto-renewal preference</Link> to renew this policy is
                you&apos;re happy with your renewal invite.
              </p>
            ),
            showRenewalDate: true,
            showAutoRenewalPreference: true,
            documentsLinkText: 'Renewal documents',
            getActionButtonConfig: ({ isAgent, dispatch, history, policyRef }: LinkConfigProps) => [
              {
                text: 'Renew now',
                to: '',
                onClick: () => {
                  if (isAgent || customerRenewalEnabled) {
                    sendAnalytics({
                      event: GTM_EVENT_TYPES.RENEW_NOW,
                      detail: {
                        is_auto_renewal: false,
                        policy_ref: policyRef,
                        usertype: analyticsUserType(isAgent)
                      }
                    });
                    sendAnalytics({
                      event: GTM_EVENT_TYPES.QINTERACTION,
                      detail: {
                        qinteraction_type: Q_INTERACTION_TYPES.PRIMARY_ACTION_BUTTON_CLICK,
                        qinteraction_locator: 'dashboard_cta',
                        qinteraction_value: 'renew_now',
                        usertype: analyticsUserType(isAgent)
                      }
                    });
                    dispatch(
                      getValidConvertibleInstanceAction({
                        username,
                        transactionType: TRANSACTION_TYPE.REN,
                        isAgent,
                        questionSet: QUESTION_SET.RENEWAL_JOURNEY,
                        onError: () => history.push(`${PATH.RENEWAL}/error`)
                      })
                    );
                  } else {
                    dispatch(openCustomerFeatureNotAvailableModal());
                  }
                }
              },
              {
                text: 'Make changes',
                to: '',
                id: 'policy-button-secondary',
                onClick: () => {
                  sendAnalytics({
                    event: GTM_EVENT_TYPES.QINTERACTION,
                    detail: {
                      qinteraction_type: Q_INTERACTION_TYPES.PRIMARY_ACTION_BUTTON_CLICK,
                      qinteraction_locator: 'dashboard_cta',
                      qinteraction_value: 'make_changes',
                      usertype: analyticsUserType(isAgent)
                    }
                  });
                  history.push(PATH.MAKE_CHANGES);
                }
              }
            ],
            className: 'policy-status-text-warning',
            statusText: 'Policy expiring'
          }
        : {
            iconUrl: assetsUrl + 'website/svg/policy-time-to-renew.svg',
            getHelperText: ({ daysUntilRenewal }: HelperTextProps) => (
              <p>
                This policy is set to auto-renew in {daysUntilRenewal} days. You don&apos;t need to take any action if
                you&apos;re happy with your renewal invite.
              </p>
            ),
            showRenewalDate: true,
            showAutoRenewalPreference: true,
            documentsLinkText: 'Renewal documents',
            getActionButtonConfig: ({ isAgent, dispatch, history, policyRef }: LinkConfigProps) => [
              {
                text: 'Renew now',
                to: '',
                onClick: () => {
                  if (isAgent || customerRenewalEnabled) {
                    sendAnalytics({
                      event: GTM_EVENT_TYPES.RENEW_NOW,
                      detail: {
                        is_auto_renewal: true,
                        policy_ref: policyRef,
                        usertype: analyticsUserType(isAgent)
                      }
                    });
                    sendAnalytics({
                      event: GTM_EVENT_TYPES.QINTERACTION,
                      detail: {
                        qinteraction_type: Q_INTERACTION_TYPES.PRIMARY_ACTION_BUTTON_CLICK,
                        qinteraction_locator: 'dashboard_cta',
                        qinteraction_value: 'renew_now',
                        usertype: analyticsUserType(isAgent)
                      }
                    });
                    dispatch(
                      getValidConvertibleInstanceAction({
                        username,
                        transactionType: TRANSACTION_TYPE.REN,
                        isAgent,
                        questionSet: QUESTION_SET.RENEWAL_JOURNEY,
                        onError: () => history.push(`${PATH.RENEWAL}/error`)
                      })
                    );
                  } else {
                    dispatch(openCustomerFeatureNotAvailableModal());
                  }
                }
              },
              {
                text: 'Make changes',
                to: '',
                id: 'policy-button-secondary',
                onClick: () => {
                  sendAnalytics({
                    event: GTM_EVENT_TYPES.QINTERACTION,
                    detail: {
                      qinteraction_type: Q_INTERACTION_TYPES.PRIMARY_ACTION_BUTTON_CLICK,
                      qinteraction_locator: 'dashboard_cta',
                      qinteraction_value: 'make_changes',
                      usertype: analyticsUserType(isAgent)
                    }
                  });
                  history.push(PATH.MAKE_CHANGES);
                }
              }
            ],
            className: 'policy-status-text-standard',
            statusText: 'Time to renew'
          };

    case POLICY_LIFECYCLE_STATUS.POLICY_RENEWAL_WINDOW:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-active.svg',
        showRenewalDate: true,
        getHelperText: () => (
          <p>
            We&apos;re getting next year&apos;s renewal quote ready. We&apos;ll email you when your new renewal quote is
            available.
          </p>
        ),
        showAutoRenewalPreference: true,
        documentsLinkText: 'Policy documents',
        className: 'policy-status-text-standard',
        statusText: 'Policy active'
      };

    case POLICY_LIFECYCLE_STATUS.POLICY_RENEWAL_INVITEDNODOCUMENTS:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-active.svg',
        showRenewalDate: true,
        getHelperText: () => (
          <p>We&apos;re getting your renewal documents ready. We&apos;ll let you know when they&apos;re available.</p>
        ),
        showAutoRenewalPreference: true,
        documentsLinkText: 'Policy documents',
        className: 'policy-status-text-standard',
        statusText: 'Policy active'
      };
    case POLICY_LIFECYCLE_STATUS.POLICY_RENEWAL_QUOTE:
    case POLICY_LIFECYCLE_STATUS.POLICY_RENEWAL_NOCONVERSION:
    case POLICY_LIFECYCLE_STATUS.POLICY_RENINVITE_WITHDRAWN:
    case POLICY_LIFECYCLE_STATUS.CANCEL_SCHEDULED:
    case POLICY_LIFECYCLE_STATUS.POLICY_RENEWAL_MTA:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-action-required.svg',
        getHelperText: () => (
          <p>
            We need to speak with you before renewing this policy. Please call us on{' '}
            <span className="bold-text">0330 660 1000</span>.
          </p>
        ),
        showRenewalDate: true,
        showAutoRenewalPreference: true,
        getActionButtonConfig: () => [
          {
            text: 'Contact us',
            to: EXTERNAL_PATH.CONTACT_US,
            isExternal: true
          }
        ],
        className: 'policy-status-text-warning',
        statusText: 'Contact us to renew'
      };

    case POLICY_LIFECYCLE_STATUS.POLICY_RENEWAL_SCHEDULED:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-time-to-renew.svg',
        getHelperText: () => (
          <p>Your policy is now scheduled to renew. Get in touch if you want to make any changes.</p>
        ),
        showRenewalDate: true,
        showAutoRenewalPreference: false,
        documentsLinkText: 'Renewal documents',
        getActionButtonConfig: () => [
          {
            text: 'Contact us',
            to: EXTERNAL_PATH.CONTACT_US,
            isExternal: true
          }
        ],
        className: 'policy-status-text-standard',
        statusText: 'Renewal scheduled'
      };

    case POLICY_LIFECYCLE_STATUS.POLICY_LIVE_EXPIRING:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-action-required.svg',
        getHelperText: () => (
          <p>
            Your policy has now expired. To reinstate your cover,{' '}
            <ExternalLink href={EXTERNAL_PATH.CONTACT_US} id="contact-us">
              contact us
            </ExternalLink>
            .
          </p>
        ),
        showRenewalDate: false,
        showAutoRenewalPreference: false,
        getActionButtonConfig: ({ isAgent, dispatch, history, policyRef }: LinkConfigProps) => [
          {
            text: 'Renew now',
            to: '',
            onClick: () => {
              if (isAgent || customerRenewalEnabled) {
                sendAnalytics({
                  event: GTM_EVENT_TYPES.RENEW_NOW,
                  detail: {
                    is_auto_renewal: !stopRenewal,
                    policy_ref: policyRef,
                    usertype: analyticsUserType(isAgent)
                  }
                });
                sendAnalytics({
                  event: GTM_EVENT_TYPES.QINTERACTION,
                  detail: {
                    qinteraction_type: Q_INTERACTION_TYPES.PRIMARY_ACTION_BUTTON_CLICK,
                    qinteraction_locator: 'dashboard_cta',
                    qinteraction_value: 'renew_now',
                    usertype: analyticsUserType(isAgent)
                  }
                });
                dispatch(
                  getValidConvertibleInstanceAction({
                    username,
                    transactionType: TRANSACTION_TYPE.REN,
                    isAgent,
                    questionSet: QUESTION_SET.RENEWAL_JOURNEY
                  })
                );
              } else {
                dispatch(openCustomerFeatureNotAvailableModal());
              }
            }
          },
          {
            text: 'Make changes',
            to: '',
            id: 'policy-button-secondary',
            onClick: () => {
              sendAnalytics({
                event: GTM_EVENT_TYPES.QINTERACTION,
                detail: {
                  qinteraction_type: Q_INTERACTION_TYPES.PRIMARY_ACTION_BUTTON_CLICK,
                  qinteraction_locator: 'dashboard_cta',
                  qinteraction_value: 'make_changes',
                  usertype: analyticsUserType(isAgent)
                }
              });
              history.push(PATH.MAKE_CHANGES);
            }
          }
        ],
        className: 'policy-status-text-warning',
        statusText: 'Policy expired'
      };
    default:
      return {
        iconUrl: assetsUrl + 'website/svg/policy-action-required.svg',
        getHelperText: () => (
          <p>
            We need to speak with you before renewing this policy. Please call us on{' '}
            <span className="bold-text">0330 660 1000</span>.
          </p>
        ),
        showRenewalDate: true,
        showAutoRenewalPreference: true,
        getActionButtonConfig: () => [
          {
            text: 'Contact us',
            to: EXTERNAL_PATH.CONTACT_US,
            isExternal: true
          }
        ],
        className: 'policy-status-text-warning',
        statusText: 'Contact us to renew'
      };
  }
};
